/** A Receipt preset is a set of customizable receipt configurations that control the information, design, and branding of receipts to align with a site's business needs. */
export interface ReceiptPreset {
  /**
   * Receipt preset ID.
   * @format GUID
   * @readonly
   * @immutable
   */
  id?: string | null;
  /**
   * Revision number, which increments by 1 each time the receipt preset is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the receipt preset.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the receipt preset was created.
   * @readonly
   * @immutable
   */
  createdDate?: Date | null;
  /**
   * Date and time the receipt preset was last updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Receipt preset name.
   * @maxLength 100
   */
  name?: string;
  /** Display settings. */
  displaySettings?: DisplaySettings;
  /**
   * Custom fields.
   * Values appear on the receipt in the specified order.
   */
  customFields?: CustomFields;
  /**
   * Whether this is the default receipt preset.
   * The default preset is used during receipt creation when a preset isn't specified or doesn't exist.
   * @readonly
   */
  default?: boolean;
  /** Display values. */
  displayValues?: DisplayValues;
  /**
   * Custom field data for the receipt preset.
   *
   * Extended fields must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
}

export interface DisplaySettings {
  /** Business display options. */
  businessDisplayOptions?: BusinessDisplayOptions;
  /** Customer display options. */
  customerDisplayOptions?: CustomerDisplayOptions;
  /** Item display options. */
  itemsDisplayOptions?: ItemsDisplayOption;
  /** Totals display options. */
  totalsDisplayOptions?: TotalsDisplayOption;
}

export interface BusinessDisplayOptions {
  /** Whether to display the business email in the receipt. */
  showBusinessEmail?: boolean;
  /** Whether to display the business phone number in the receipt. */
  showBusinessPhoneNumber?: boolean;
  /** Whether to display the business address in the receipt. */
  showBusinessAddress?: boolean;
}

export interface CustomerDisplayOptions {
  /** Whether to display the customer's email in the receipt. */
  showCustomerEmail?: boolean;
  /** Whether to display the customer's phone number in the receipt. */
  showCustomerPhoneNumber?: boolean;
  /** Whether to display the customer's shipping address in the receipt. */
  showCustomerShippingAddress?: boolean;
  /** Whether to display the customer's billing address in the receipt. */
  showCustomerBillingAddress?: boolean;
  /** Whether to display the customer's VAT ID in the receipt. */
  showCustomerVatId?: boolean;
  /** Whether to display the customer's company's name. */
  showCustomerCompanyName?: boolean;
}

export interface ItemsDisplayOption {
  /** Whether to display item descriptions in the receipt. */
  showItemsDescription?: boolean;
  /** Whether to display item taxes in the receipt. */
  showTax?: boolean;
}

export interface TotalsDisplayOption {
  /** Whether to display the tax breakdown in the receipt. */
  showTaxBreakdown?: boolean;
  /** Whether to display the item subtotal per tax breakdown in the receipt. */
  showItemSubtotalPerTaxBreakdown?: boolean;
}

export interface CustomFields {
  /**
   * Header custom fields.
   * @maxSize 4
   */
  headerCustomFields?: CustomField[];
  /**
   * Business custom fields.
   * @maxSize 4
   */
  businessCustomFields?: CustomField[];
  /**
   * Customer custom fields.
   * @maxSize 4
   */
  customerCustomFields?: CustomField[];
  /**
   * Footer custom fields.
   * @maxSize 4
   */
  footerCustomFields?: FooterCustomField[];
}

export interface CustomField {
  /**
   * Title.
   * @minLength 1
   * @maxLength 100
   */
  title?: string | null;
  /**
   * Value.
   * @maxLength 100
   */
  value?: string;
}

export interface FooterCustomField {
  /**
   * Title.
   * @minLength 1
   * @maxLength 200
   */
  title?: string;
  /**
   * Value.
   * @maxLength 5000
   */
  value?: string | null;
}

export interface DisplayValues {
  /**
   * Text override for receipt title.
   * @minLength 1
   * @maxLength 20
   */
  titleOverride?: string | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface CreateReceiptPresetRequest {
  /** Receipt preset to create. */
  receiptPreset: ReceiptPreset;
}

export interface CreateReceiptPresetResponse {
  /** Created receipt preset. */
  receiptPreset?: ReceiptPreset;
}

export interface GetReceiptPresetRequest {
  /**
   * Receipt preset ID to retrieve.
   * @format GUID
   */
  receiptPresetId: string;
}

export interface GetReceiptPresetResponse {
  /** Requested receipt preset. */
  receiptPreset?: ReceiptPreset;
}

export interface GetDefaultReceiptPresetRequest {}

export interface GetDefaultReceiptPresetResponse {
  /** Default receipt preset. */
  receiptPreset?: ReceiptPreset;
}

export interface UpdateReceiptPresetRequest {
  /** Receipt preset to update. May be partial. */
  receiptPreset: ReceiptPreset;
}

export interface UpdateReceiptPresetResponse {
  /** Updated receipt preset. */
  receiptPreset?: ReceiptPreset;
}

export interface SetDefaultReceiptPresetRequest {
  /**
   * Receipt preset ID to set as default.
   * @format GUID
   */
  receiptPresetId: string;
}

export interface SetDefaultReceiptPresetResponse {
  /** Default receipt preset. */
  receiptPreset?: ReceiptPreset;
}

export interface DeleteReceiptPresetRequest {
  /**
   * Receipt preset ID to delete.
   * @format GUID
   */
  receiptPresetId: string;
}

export interface DeleteReceiptPresetResponse {}

export interface ListReceiptPresetsRequest {}

export interface ListReceiptPresetsResponse {
  /** List of receipt presets. */
  receiptPresets?: ReceiptPreset[];
}

export interface UpdateExtendedFieldsRequest {
  /** ID of the entity to update. */
  id: string;
  /** Identifier for the app whose extended fields are being updated. */
  namespace: string;
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

export interface UpdateExtendedFieldsResponse {
  /** Updated receipt preset. */
  receiptPreset?: ReceiptPreset;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateReceiptPresetApplicationErrors =
  | {
      code?: 'NO_META_SITE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'APP_INSTALLATION_FAILED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type GetDefaultReceiptPresetApplicationErrors = {
  code?: 'NO_META_SITE';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type UpdateReceiptPresetApplicationErrors = {
  code?: 'NO_META_SITE';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type DeleteReceiptPresetApplicationErrors =
  | {
      code?: 'NO_META_SITE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DEFAULT_RECEIPT_PRESET';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type ListReceiptPresetsApplicationErrors = {
  code?: 'NO_META_SITE';
  description?: string;
  data?: Record<string, any>;
};
